import { useState, useEffect } from 'react';
import {
  Database,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Loader2,
  Clock,
  Settings,
  BookOpen,
  Briefcase,
  Calendar,
  Download,
  Upload,
  FileSpreadsheet,
  Trash2
} from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';
import { useConfirm } from '../ui';

interface ESCOSyncStatus {
  id: string;
  esco_version: string;
  synced_at: string;
  skills_count: number;
  occupations_count: number;
  relations_count: number;
  sync_duration_ms: number;
  status: string;
  error_message?: string;
}

interface ESCOSyncSettings {
  sync_enabled: boolean;
  sync_frequency_days: number;
  last_check_at: string | null;
}

interface ESCOStatistics {
  skills_count: number;
  occupations_count: number;
}

export default function ESCOSettings() {
  const { confirm } = useConfirm();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // CSV Import state
  const [importing, setImporting] = useState<'skills' | 'occupations' | null>(null);
  const [clearing, setClearing] = useState(false);

  const [syncStatus, setSyncStatus] = useState<ESCOSyncStatus | null>(null);
  const [settings, setSettings] = useState<ESCOSyncSettings>({
    sync_enabled: true,
    sync_frequency_days: 7,
    last_check_at: null
  });
  const [statistics, setStatistics] = useState<ESCOStatistics>({
    skills_count: 0,
    occupations_count: 0
  });
  const [syncHistory, setSyncHistory] = useState<ESCOSyncStatus[]>([]);

  // Fetch ESCO sync status - returns the status for polling
  const fetchStatus = async (): Promise<ESCOSyncStatus | null> => {
    try {
      const response = await authenticatedFetch('/api/esco/local/sync/status');
      if (response.ok) {
        const data = await response.json();
        setSyncStatus(data.last_sync);
        setSettings(data.settings);
        setStatistics(data.statistics);
        return data.last_sync;
      }
    } catch (err) {
      console.error('Failed to fetch ESCO status:', err);
    }
    return null;
  };

  // Fetch sync history
  const fetchHistory = async () => {
    try {
      const response = await authenticatedFetch('/api/esco/local/sync/history?limit=5');
      if (response.ok) {
        const data = await response.json();
        setSyncHistory(data.history || []);
      }
    } catch (err) {
      console.error('Failed to fetch ESCO history:', err);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStatus(), fetchHistory()]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Track sync progress message
  const [syncProgress, setSyncProgress] = useState('');

  // Trigger manual sync
  const handleManualSync = async () => {
    setSyncing(true);
    setError('');
    setSuccess('');
    setSyncProgress('Starter synkronisering...');

    try {
      const response = await authenticatedFetch('/api/esco/local/sync/trigger', {
        method: 'POST'
      });

      if (response.ok) {
        setSyncProgress('Synkronisering startet. Laster ned data fra EU ESCO API...');

        let pollCount = 0;
        // Poll for updates - use returned status directly instead of stale state
        const pollInterval = setInterval(async () => {
          pollCount++;
          const currentStatus = await fetchStatus();

          // Update progress message based on poll count
          if (pollCount <= 6) {
            setSyncProgress(`Laster ned ferdigheter... (ca. ${pollCount * 5} sekunder)`);
          } else if (pollCount <= 12) {
            setSyncProgress(`Laster ned yrkesroller... (ca. ${pollCount * 5} sekunder)`);
          } else {
            setSyncProgress(`Importerer data til database... (ca. ${pollCount * 5} sekunder)`);
          }

          // Check the freshly fetched status, not stale state
          if (currentStatus && currentStatus.status !== 'running') {
            clearInterval(pollInterval);
            setSyncing(false);
            setSyncProgress('');
            await fetchHistory();

            if (currentStatus.status === 'completed') {
              setSuccess(`Synkronisering fullført! ${currentStatus.skills_count?.toLocaleString() || 0} ferdigheter og ${currentStatus.occupations_count?.toLocaleString() || 0} yrkesroller importert på ${formatDuration(currentStatus.sync_duration_ms)}.`);
            } else if (currentStatus.status === 'failed') {
              setError(`Synkronisering feilet: ${currentStatus.error_message || 'Ukjent feil'}`);
            }
          }
        }, 5000);

        // Safety timeout after 10 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          if (syncing) {
            setSyncing(false);
            setSyncProgress('');
            setError('Synkronisering tok for lang tid. Sjekk status manuelt.');
            fetchStatus();
            fetchHistory();
          }
        }, 600000); // 10 minutes max
      } else {
        const data = await response.json();
        setError(data.error || 'Kunne ikke starte synkronisering');
        setSyncing(false);
        setSyncProgress('');
      }
    } catch (err: any) {
      setError(err.message || 'Nettverksfeil');
      setSyncing(false);
      setSyncProgress('');
    }
  };

  // Save settings
  const handleSaveSettings = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await authenticatedFetch('/api/esco/local/sync/settings', {
        method: 'POST',
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        setSuccess('Innstillinger lagret');
      } else {
        const data = await response.json();
        setError(data.error || 'Kunne ikke lagre innstillinger');
      }
    } catch (err: any) {
      setError(err.message || 'Nettverksfeil');
    } finally {
      setSaving(false);
    }
  };

  // Handle CSV file upload
  const handleCSVUpload = async (type: 'skills' | 'occupations', file: File, language: string) => {
    setImporting(type);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('language', language);

      const response = await authenticatedFetch(`/api/esco/local/import/${type}`, {
        method: 'POST',
        body: formData,
        headers: {
          // Don't set Content-Type - let browser set it with boundary
        }
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(`Importert ${data.imported?.toLocaleString() || 0} ${type === 'skills' ? 'ferdigheter' : 'yrkesroller'}. ${data.errors > 0 ? `(${data.errors} feil)` : ''}`);
        await fetchStatus();
        await fetchHistory();
      } else {
        setError(data.error || `Kunne ikke importere ${type}`);
      }
    } catch (err: any) {
      setError(err.message || 'Nettverksfeil under opplasting');
    } finally {
      setImporting(null);
    }
  };

  // Clear ESCO data
  const handleClearData = async (type: 'skills' | 'occupations' | 'all') => {
    const typeLabel = type === 'all' ? 'alle ESCO-data' : type === 'skills' ? 'alle ferdigheter' : 'alle yrkesroller';
    const confirmed = await confirm({
      title: 'Slett data',
      message: `Er du sikker på at du vil slette ${typeLabel}? Dette kan ikke angres.`,
      confirmLabel: 'Slett',
      cancelLabel: 'Avbryt',
      variant: 'danger'
    });

    if (!confirmed) return;

    setClearing(true);
    setError('');
    setSuccess('');

    try {
      const response = await authenticatedFetch(`/api/esco/local/clear?type=${type}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message || 'Data slettet');
        await fetchStatus();
      } else {
        setError(data.error || 'Kunne ikke slette data');
      }
    } catch (err: any) {
      setError(err.message || 'Nettverksfeil');
    } finally {
      setClearing(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('nb-NO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (ms: number) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}min`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-kf-blue" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Database className="w-6 h-6 text-kf-blue" />
            ESCO Taksonomi
          </h2>
          <p className="text-sm text-kf-gray-600 mt-1">
            European Skills, Competences, Qualifications and Occupations
          </p>
        </div>
        <a
          href="https://esco.ec.europa.eu"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-kf-blue hover:underline"
        >
          esco.ec.europa.eu
        </a>
      </div>

      {/* Status messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          {success}
        </div>
      )}

      {/* Statistics cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BookOpen className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-kf-gray-600">Ferdigheter</p>
              <p className="text-2xl font-bold">{statistics.skills_count.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Briefcase className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-kf-gray-600">Yrkesroller</p>
              <p className="text-2xl font-bold">{statistics.occupations_count.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Calendar className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-kf-gray-600">Sist synkronisert</p>
              <p className="text-lg font-semibold">
                {syncStatus ? formatDate(syncStatus.synced_at) : 'Aldri'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Sync section */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <RefreshCw className="w-5 h-5" />
          Synkronisering
        </h3>

        <div className="space-y-4">
          {/* Last sync info */}
          {syncStatus && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-kf-gray-600">Versjon:</span>
                  <span className="ml-2 font-medium">{syncStatus.esco_version}</span>
                </div>
                <div>
                  <span className="text-kf-gray-600">Status:</span>
                  <span className={`ml-2 font-medium ${
                    syncStatus.status === 'completed' ? 'text-green-600' :
                    syncStatus.status === 'failed' ? 'text-red-600' :
                    syncStatus.status === 'running' ? 'text-blue-600' : ''
                  }`}>
                    {syncStatus.status === 'completed' ? 'Fullfort' :
                     syncStatus.status === 'failed' ? 'Feilet' :
                     syncStatus.status === 'running' ? 'Kjorer...' : syncStatus.status}
                  </span>
                </div>
                <div>
                  <span className="text-kf-gray-600">Varighet:</span>
                  <span className="ml-2 font-medium">{formatDuration(syncStatus.sync_duration_ms)}</span>
                </div>
                <div>
                  <span className="text-kf-gray-600">Ferdigheter:</span>
                  <span className="ml-2 font-medium">{syncStatus.skills_count?.toLocaleString() || 0}</span>
                </div>
              </div>
              {syncStatus.error_message && (
                <div className="mt-2 text-sm text-red-600">
                  Feil: {syncStatus.error_message}
                </div>
              )}
            </div>
          )}

          {/* Manual sync button */}
          <div className="space-y-3">
            <div className="flex items-center gap-4">
              <button
                onClick={handleManualSync}
                disabled={syncing}
                className="btn-primary flex items-center gap-2"
              >
                {syncing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Synkroniserer...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Start manuell synkronisering
                  </>
                )}
              </button>
              {!syncing && (
                <p className="text-sm text-kf-gray-600">
                  Laster ned ESCO-data fra EU. Dette kan ta flere minutter.
                </p>
              )}
            </div>

            {/* Progress indicator during sync */}
            {syncing && syncProgress && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-800">{syncProgress}</p>
                    <p className="text-sm text-blue-600 mt-1">
                      Prosessen kjører i bakgrunnen. Ikke lukk denne siden.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CSV Import section */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <FileSpreadsheet className="w-5 h-5" />
          CSV-import
        </h3>

        <div className="space-y-6">
          {/* Instructions */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h4 className="font-medium text-amber-800 mb-2">Slik laster du ned ESCO CSV-filer</h4>
            <ol className="text-sm text-amber-700 list-decimal list-inside space-y-1">
              <li>Gå til <a href="https://esco.ec.europa.eu/en/use-esco/download" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-900">esco.ec.europa.eu/en/use-esco/download</a></li>
              <li>Velg versjon (f.eks. v1.2.1), innholdstype (Skills/Occupations), språk og CSV-format</li>
              <li>Oppgi e-postadresse for å motta nedlastingslenke</li>
              <li>Last ned CSV-filene og importer dem her</li>
            </ol>
          </div>

          {/* Skills import */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium">Importer ferdigheter</h4>
                  <p className="text-sm text-kf-gray-600">Last opp skills CSV-fil fra ESCO</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <select
                id="skills-language"
                className="input w-32"
                defaultValue="en"
              >
                <option value="en">Engelsk</option>
                <option value="no">Norsk</option>
              </select>
              <label className="flex-1">
                <input
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    const lang = (document.getElementById('skills-language') as HTMLSelectElement)?.value || 'en';
                    if (file) handleCSVUpload('skills', file, lang);
                    e.target.value = ''; // Reset for same file
                  }}
                  disabled={!!importing}
                />
                <span className={`btn-secondary flex items-center gap-2 cursor-pointer ${importing === 'skills' ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  {importing === 'skills' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Importerer...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Velg CSV-fil
                    </>
                  )}
                </span>
              </label>
              <button
                onClick={() => handleClearData('skills')}
                disabled={clearing || !!importing}
                className="btn-danger-outline flex items-center gap-2"
                title="Slett alle ferdigheter"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Occupations import */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Briefcase className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium">Importer yrkesroller</h4>
                  <p className="text-sm text-kf-gray-600">Last opp occupations CSV-fil fra ESCO</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <select
                id="occupations-language"
                className="input w-32"
                defaultValue="en"
              >
                <option value="en">Engelsk</option>
                <option value="no">Norsk</option>
              </select>
              <label className="flex-1">
                <input
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    const lang = (document.getElementById('occupations-language') as HTMLSelectElement)?.value || 'en';
                    if (file) handleCSVUpload('occupations', file, lang);
                    e.target.value = '';
                  }}
                  disabled={!!importing}
                />
                <span className={`btn-secondary flex items-center gap-2 cursor-pointer ${importing === 'occupations' ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  {importing === 'occupations' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Importerer...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Velg CSV-fil
                    </>
                  )}
                </span>
              </label>
              <button
                onClick={() => handleClearData('occupations')}
                disabled={clearing || !!importing}
                className="btn-danger-outline flex items-center gap-2"
                title="Slett alle yrkesroller"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Clear all data */}
          <div className="pt-4 border-t">
            <button
              onClick={() => handleClearData('all')}
              disabled={clearing || !!importing}
              className="btn-danger flex items-center gap-2"
            >
              {clearing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              Slett alle ESCO-data
            </button>
            <p className="text-sm text-kf-gray-600 mt-2">
              Slett alle data for å starte på nytt med nye CSV-filer.
            </p>
          </div>
        </div>
      </div>

      {/* Settings section */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Innstillinger
        </h3>

        <div className="space-y-4">
          {/* Enable/disable sync */}
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium">Automatisk synkronisering</label>
              <p className="text-sm text-kf-gray-600">
                Sjekk for oppdateringer automatisk
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.sync_enabled}
                onChange={(e) => setSettings({ ...settings, sync_enabled: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-kf-blue"></div>
            </label>
          </div>

          {/* Sync frequency */}
          <div>
            <label className="block font-medium mb-2">Synkroniseringsfrekvens</label>
            <select
              value={settings.sync_frequency_days}
              onChange={(e) => setSettings({ ...settings, sync_frequency_days: parseInt(e.target.value) })}
              className="input w-full max-w-xs"
            >
              <option value={7}>Ukentlig (anbefalt)</option>
              <option value={14}>Annenhver uke</option>
              <option value={30}>Manedlig</option>
              <option value={90}>Kvartalsvis</option>
            </select>
            <p className="text-sm text-kf-gray-600 mt-1">
              ESCO oppdateres sjelden (1-2 ganger per ar). Ukentlig sjekk er tilstrekkelig.
            </p>
          </div>

          {/* Save button */}
          <div className="pt-4">
            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="btn-primary flex items-center gap-2"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              Lagre innstillinger
            </button>
          </div>
        </div>
      </div>

      {/* Sync history */}
      {syncHistory.length > 0 && (
        <div className="bg-white border rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Synkroniseringslogg
          </h3>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3">Tidspunkt</th>
                  <th className="text-left py-2 px-3">Versjon</th>
                  <th className="text-left py-2 px-3">Status</th>
                  <th className="text-left py-2 px-3">Ferdigheter</th>
                  <th className="text-left py-2 px-3">Yrkesroller</th>
                  <th className="text-left py-2 px-3">Varighet</th>
                </tr>
              </thead>
              <tbody>
                {syncHistory.map((entry) => (
                  <tr key={entry.id} className="border-b last:border-b-0">
                    <td className="py-2 px-3">{formatDate(entry.synced_at)}</td>
                    <td className="py-2 px-3 font-mono text-xs">{entry.esco_version}</td>
                    <td className="py-2 px-3">
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        entry.status === 'completed' ? 'bg-green-100 text-green-700' :
                        entry.status === 'failed' ? 'bg-red-100 text-red-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {entry.status === 'completed' ? 'Fullfort' :
                         entry.status === 'failed' ? 'Feilet' : entry.status}
                      </span>
                    </td>
                    <td className="py-2 px-3">{entry.skills_count?.toLocaleString() || '-'}</td>
                    <td className="py-2 px-3">{entry.occupations_count?.toLocaleString() || '-'}</td>
                    <td className="py-2 px-3">{formatDuration(entry.sync_duration_ms)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-800 mb-2">Om ESCO</h4>
        <p className="text-sm text-blue-700">
          ESCO (European Skills, Competences, Qualifications and Occupations) er EUs
          flerspraklige klassifisering av ferdigheter, kompetanser og yrker. Lokal
          lagring gir raskere oppslag og bedre fuzzy-matching for norske laeringsutbytter.
        </p>
        <p className="text-sm text-blue-700 mt-2">
          Ferdigheter kobles automatisk til laeringsutbytter i studieplaner og moduler.
          Yrkesroller kobles til malgruppe og opptakskrav.
        </p>
      </div>
    </div>
  );
}
